import { SelectHTMLAttributes, forwardRef } from 'react';

interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  options: SelectOption[];
  error?: string;
  placeholder?: string;
}

/**
 * Select dropdown component — accessible with label and error state.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, options, error, placeholder, id, className = '', ...props }, ref) => {
    const selectId = id || `select-${label.toLowerCase().replace(/\s/g, '-')}`;
    const errorId = `${selectId}-error`;

    return (
      <div className={`form-group ${error ? 'form-group-error' : ''} ${className}`}>
        <label htmlFor={selectId}>{label}</label>
        <select
          ref={ref}
          id={selectId}
          aria-invalid={!!error}
          aria-describedby={error ? errorId : undefined}
          {...props}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {error && (
          <small id={errorId} className="form-error" role="alert">
            {error}
          </small>
        )}
      </div>
    );
  },
);

Select.displayName = 'Select';
